// One conversation: glass header, the timeline on the wallpaper, the floating composer.
//
// The timeline is a `column-reverse` scroller: its natural resting place IS the newest message,
// so a conversation opens at the bottom on its FIRST frame with no scroll-to-bottom jump, older
// history added at the top never moves what the reader is looking at, and `content-visibility`
// lets the browser skip laying out rows that are off screen. That is the whole virtualisation —
// the Mac needed an AppKit table and six releases of anchoring fixes (ADR-002) for what the
// browser's own layout gives here for free.
import { DayPill } from '../features/extras/DayPill';
import { DiagnosticsButton } from '../features/extras/Diagnostics';
import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent, type ReactNode, type WheelEvent as ReactWheelEvent } from 'react';
import { data } from '../../bridge/client';
import type { ChannelPage } from '../../bridge/protocol';
import { userDisplayName } from '../../core/channelListDerivation';
import { ConversationLanding } from '../../core/conversationLanding';
import { FILL_MAX_PAGES, FILL_MIN_ROOTS, HistoryFill } from '../../core/historyRange';
import { PostState, type PostRecord } from '../../core/records';
import { TypingIndicatorLabel } from '../../core/typingIndicatorLabel';
import { Icon } from '../../design/icons';
import { t, tf } from '../../i18n/i18n';
import { useLive, useMinuteClock } from '../hooks';
import { buildConversation, type ConversationModel } from '../models/conversationModel';
import { useMentionOpener } from '../features/threads/mentionOpen';
import { count, endChannelSwitch } from '../perf';
import { share } from '../../core/structuralShare';
import { RowKeys } from '../../core/rowIdentity';
import { cachedPage, FIRST_PAGE, remember, takeInflight } from '../pageCache';
import type { SendExtras } from '../../bridge/protocol';
import { Composer } from './Composer';
import { useMentionDirectory } from './knownUsernames';
import { rememberEchoFiles } from '../features/attachments/localFiles';
import { MessageRow, type RowActions } from './MessageRow';
import { useConversationPermissions } from './conversationPermissions';
import { editRights } from '../../core/messageEditRights';
import { useRowActions } from './rowActions';
import { rememberEmoji, useQuickReactionNames } from '../features/emoji/recents';
import { CallConversationStrips, CallHeaderButton } from '../features/calls/CallSurfaces';
import { AppPluginActions, PluginNoticeStrip, useRegistryPresence, type PluginNotice } from '../features/appPlugins/AppPluginActions';
import type { BodyContext } from './MessageBody';
import { useDmPresenceHeader } from '../features/presence/DmPresence';
import { useViewers, useViewingHeartbeat } from '../features/viewing/viewing';
import { ViewingStack } from '../features/viewing/ViewingStack';
import { ViewingPresence } from '../../core/viewingPresence';
import { BookmarksBar } from '../features/bookmarks/BookmarksBar';
import { UndeliveredStrip } from '../features/undelivered/UndeliveredStrip';
import { useConversationAnnouncements } from '../a11y/useConversationAnnouncements';
import { ShellContext } from '../shell/ShellContext';

const PAGE_STEP = 30;
/** Steps in a row that added no root before the top waits for the reader (Windows: 5). */
const MAX_BARREN_RUN = 5;

/** Open ON a message (search hit, saved, permalink): the window around it, flashed, with the way back. */
export interface JumpAnchor {
  channelId: string;
  postId: string;
  createAt: number;
  token: number;
}

export interface ConversationProps {
  channelId: string;
  previousChannelId: string | null;
  title: string;
  subtitle: string;
  /** `area`: a Core área channel (shared/API.md §37) — the people glyph. */
  icon: 'hash' | 'lock' | 'dm' | 'group' | 'area';
  unreadBoundary: number | null;
  me: string;
  myUsername: string | null;
  typists: string[];
  readOnlyNotice: string | null;
  /** An archived channel: read-only history, no call/plugin actions, no «be the first» invitation. */
  archived?: boolean;
  onOpenThread(rootId: string): void;
  onOpenUser(username: string, anchor?: { x: number; y: number }): void;
  onOpenUserId(userId: string, anchor?: { x: number; y: number }): void;
  onOpenInfo(tab?: 'info' | 'members' | 'pinned' | 'files'): void;
  onSearchHere(): void;
  onToast(message: string, tone?: 'info' | 'error'): void;
  teamName: string;
  jump: JumpAnchor | null;
  onLeaveJump(): void;
  connectionStrip: string | null;
  headerLeading?: ReactNode;
  /** The DM partner's photo + presence dot / a group's faces; channels keep their glyph. */
  headerFace?: ReactNode;
  /** A DM: whose presence line and device glyph the header shows (`noPhrase`: bot / self-DM). */
  dmPartner?: { userId: string; username: string; noPhrase: boolean } | null;
}

const NO_CUSTOM_EMOJI: Record<string, { id: string; updateAt: number }> = {};
const PAGE_TABLES = ['rootReplyStats', 'posts', 'postFiles', 'postReactions', 'users', 'postSeenSummaries', 'userStatuses', 'channels', 'customEmoji', 'postAcknowledgements'] as const;

// One recents list for the picker, the hover bar and the menu (features/emoji/recents.ts): a pick
// anywhere re-renders the quick row everywhere.
export function useQuickReactions(): string[] {
  return useQuickReactionNames(6);
}

export function rememberReaction(name: string): void {
  rememberEmoji(name);
}

export function Conversation(p: ConversationProps) {
  const shell = useContext(ShellContext);
  const [limit, setLimit] = useState(FIRST_PAGE);
  const [echoes, setEchoes] = useState<PostRecord[]>([]);
  const [olderState, setOlderState] = useState<{ loading: boolean; failed: boolean; hasMore: boolean }>({ loading: false, failed: false, hasMore: true });
  const [atBottom, setAtBottom] = useState(true);
  const scroller = useRef<HTMLDivElement>(null);
  const topSentinel = useRef<HTMLDivElement>(null);
  const now = useMinuteClock();
  const quickReactions = useQuickReactions();
  const switchReported = useRef(false);
  const [pluginNotice, setPluginNotice] = useState<PluginNotice | null>(null);
  useRegistryPresence(p.channelId);
  // «Viendo ahora» (shared/API.md §39): this pane is on screen → heartbeats; the others' faces in the header.
  useViewingHeartbeat(p.channelId, '');
  const viewers = useViewers(p.channelId, '');
  const partnerViewing = !!p.dmPartner && ViewingPresence.partnerIsViewing(viewers, p.dmPartner.userId, p.me);
  const dmHeader = useDmPresenceHeader(p.dmPartner?.userId ?? '', p.dmPartner?.username ?? '', !p.dmPartner || p.dmPartner.noPhrase, partnerViewing);

  const anchored = p.jump;
  const { value: page, loadedFor } = useLive<ChannelPage>(
    () => (anchored ? data.call('channelPageAround', p.channelId, anchored.createAt) : data.call('channelPage', p.channelId, limit)),
    [...PAGE_TABLES],
    [p.channelId, limit, anchored?.token ?? 0],
    {
      // The click already asked (pageCache.prefetchPage on mousedown); a revisited conversation
      // paints its last page on the first frame while the store is re-read underneath.
      initial: () => (anchored ? undefined : cachedPage(p.channelId) ?? undefined),
      first: () => (!anchored && limit === FIRST_PAGE ? takeInflight(p.channelId) : null),
    },
  );
  // The jumped-to message: centred and marked, resting at half strength for twelve seconds
  // (JumpBlink.holdMillis) — three blinks and gone was the complaint fixed on every client.
  const [markedId, setMarkedId] = useState<string | null>(null);
  useEffect(() => {
    if (!anchored) return;
    setMarkedId(anchored.postId);
    const id = setTimeout(() => setMarkedId(null), 12_000);
    return () => clearTimeout(id);
  }, [anchored?.token]);
  // Reset on a new conversation; open it (optimistic read clear + newest page in the background).
  useEffect(() => {
    setLimit(FIRST_PAGE);
    setEchoes([]);
    setOlderState({ loading: false, failed: false, hasMore: true });
    barrenRun.current = 0;
    fillPages.current = 0;
    setLanded(false);
    switchReported.current = false;
    let alive = true;
    void data.call('openChannel', p.channelId, p.previousChannelId).then((r) => {
      if (!alive) return;
      setOlderState((s) => ({ ...s, hasMore: r.hasOlder }));
      setLanded(true);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.channelId]);

  const current = page && page.channelId === p.channelId ? page : null;
  useEffect(() => {
    if (current) count('conversation.page');
  }, [current]);
  useEffect(() => {
    if (current && !anchored && current.posts.length <= FIRST_PAGE) remember(current);
  }, [current, anchored]);
  useEffect(() => {
    if (!anchored || !current) return;
    requestAnimationFrame(() => {
      document.querySelector(`.conversation-pane:not(.side-panel) [data-post-id="${anchored.postId}"]`)?.scrollIntoView({ block: 'center' });
    });
  }, [anchored?.token, current?.posts.length]);

  // Shared with the previous build (core/structuralShare.ts): rows, chips, footers and users that did
  // not change keep their identity, so the memoized rows they feed do not re-render.
  const previousModel = useRef<ConversationModel | null>(null);
  const model = useMemo(() => {
    const built = current ? buildConversation(current, p.me, p.unreadBoundary, now, echoes.filter((e) => e.channelId === p.channelId)) : null;
    const shared = built && previousModel.current ? share(previousModel.current, built) : built;
    previousModel.current = shared;
    return shared;
  }, [current, p.me, p.unreadBoundary, now, echoes, p.channelId]);
  // A confirmed send renames its row (pendingId → server id) instead of remounting it.
  const rowKeys = useMemo(() => new RowKeys(), [p.channelId]);
  const usersById = model?.usersById;
  const footerNames = useCallback(
    (id: string) => {
      const u = usersById?.get(id);
      return { name: u ? userDisplayName(u) : '?', version: u?.lastPictureUpdate ?? 0 };
    },
    [usersById],
  );

  useEffect(() => {
    if (model) count('conversation.model');
  }, [model]);

  // Screen readers hear others' new messages (polite) and the reader's failed sends (assertive), P255.
  useConversationAnnouncements(p.channelId, current?.posts ?? null, p.me, (post) => model?.authorName(post) ?? '');

  // channel switch → messages painted (docs/PERFORMANCE.md budget: < 100 ms)
  useLayoutEffect(() => {
    if (model && !switchReported.current && (loadedFor === 'cache' || loadedFor.includes(p.channelId))) {
      switchReported.current = true;
      endChannelSwitch(p.channelId, model.rows.length);
    }
  }, [model, loadedFor, p.channelId]);

  // A row that cannot name its author goes and FETCHES the profile.
  useEffect(() => {
    if (model?.unknownAuthorIds.length) void data.call('resolveUsers', model.unknownAuthorIds);
  }, [model?.unknownAuthorIds.join(',')]);

  // Echoes yield to the stored row the moment it exists.
  useEffect(() => {
    if (!current || !echoes.length) return;
    const stored = new Set(current.posts.flatMap((x) => [x.id, x.pendingId ?? '']));
    const left = echoes.filter((e) => !stored.has(e.id));
    if (left.length !== echoes.length) setEchoes(left);
  }, [current, echoes]);

  // Older history (Windows 1.0.4, «solo veo mensajes a partir del 9 de septiembre»): the worker pages
  // from the FLOOR of the conversation's hole-free stretch — never from a visible row, which a page of
  // replies only cannot move — and the page only shows roots inside that stretch. A step that adds
  // no root (replies only) is "barren": the top asks again by itself up to MAX_BARREN_RUN times in a
  // row, then waits for the reader's own gesture (wheel up / PageUp / Home / ↑ at the top).
  const barrenRun = useRef(0);
  // FILL (hotfix 2026-10-14): while the stretch shows fewer than FILL_MIN_ROOTS roots the timeline pages
  // back by itself — a newest page of replies only drew an empty timeline that never scrolled, so
  // nothing asked. Own cap (FILL_MAX_PAGES), reset on open and on the reader's own gesture.
  const fillPages = useRef(0);
  const [landed, setLanded] = useState(false);
  const olderInflight = useRef<string | null>(null);
  const channelRef = useRef(p.channelId);
  channelRef.current = p.channelId;
  const loadOlder = useCallback(async () => {
    if (olderState.loading || olderInflight.current === p.channelId) return;
    const channelId = p.channelId;
    olderInflight.current = channelId;
    setOlderState((s) => ({ ...s, loading: true, failed: false }));
    const r = await data.call('loadOlder', channelId).finally(() => {
      if (olderInflight.current === channelId) olderInflight.current = null;
    });
    if (channelRef.current !== channelId) return;
    if (r.kind === 'failed') setOlderState((s) => ({ ...s, loading: false, failed: true }));
    else if (r.kind === 'end') setOlderState({ loading: false, failed: false, hasMore: false });
    else {
      barrenRun.current = r.roots ? 0 : barrenRun.current + 1;
      setOlderState({ loading: false, failed: false, hasMore: !!r.hasMore });
      setLimit((l) => l + PAGE_STEP);
    }
  }, [olderState.loading, p.channelId]);

  /**
   * One step up: the store first (a wider read), the server second. `byHand` = the reader's own
   * gesture (lifts the barren cap); `retry` = Reintentar (the only way past a failed step).
   */
  const askOlder = useCallback(
    (byHand: boolean, retry = false) => {
      if (anchored || !current) return;
      if (current.posts.length >= limit) {
        setLimit((l) => l + PAGE_STEP);
        return;
      }
      if (byHand) {
        barrenRun.current = 0;
        fillPages.current = 0;
      } else if (barrenRun.current >= MAX_BARREN_RUN) return;
      if (ConversationLanding.mayLoadOlder({ hasLanded: true, isLoadingOlder: olderState.loading, hasMoreOlder: olderState.hasMore, lastAttemptFailed: olderState.failed && !retry }))
        void loadOlder();
    },
    [anchored, current, limit, olderState, loadOlder],
  );

  const fillRoots = current?.posts.length ?? 0;
  const reachedStart = current?.historyReach === 'start';
  useEffect(() => {
    if (anchored || !current || !landed || olderState.loading) return;
    if (fillRoots >= FILL_MIN_ROOTS) {
      fillPages.current = 0;
      return;
    }
    if (!HistoryFill.shouldFetch({ roots: fillRoots, reachedStart, lastFailed: olderState.failed, pagesThisFill: fillPages.current })) return;
    fillPages.current += 1;
    void loadOlder();
  }, [anchored, current, landed, fillRoots, reachedStart, olderState, loadOlder]);

  // The top of history asks for more only when the reader actually reaches it, and a FAILURE
  // stops the automatic asking (a geometry trigger would otherwise retry on every layout pass).
  // The observer is rebuilt whenever the page or the paging state changes, and a fresh observer
  // reports the sentinel at once — so a step that landed without moving the list (replies only)
  // asks again while the top is still in view.
  useEffect(() => {
    const el = topSentinel.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) askOlder(false);
      },
      { root: scroller.current, rootMargin: '600px 0px 0px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [askOlder]);

  /** column-reverse: the top of history is the most negative scrollTop. */
  const nearTop = () => {
    const el = scroller.current;
    return !!el && el.scrollHeight - el.clientHeight + el.scrollTop < 600;
  };
  // The dead top: at the very top a wheel turn or PageUp moves nothing, so no scroll or intersection
  // event fires — the reader's gesture itself asks.
  const onWheel = (e: ReactWheelEvent) => {
    if (e.deltaY < 0 && nearTop()) askOlder(true);
  };
  const onTimelineKey = (e: ReactKeyboardEvent) => {
    if ((e.key === 'PageUp' || e.key === 'Home' || e.key === 'ArrowUp') && nearTop()) askOlder(true);
  };

  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    // column-reverse: 0 is the bottom, negative values go up.
    setAtBottom(el.scrollTop > -80);
  };

  const scrollToBottom = () => scroller.current?.scrollTo({ top: 0, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });

  const send = (message: string, extras: SendExtras = {}) => {
    const pendingId = crypto.randomUUID().replace(/-/g, '');
    const createAt = Date.now();
    // On screen THIS frame — "I sent it and it appeared after a while" is the send path's one
    // unforgivable failure (2026-09-04). The store row replaces the echo when it lands.
    setEchoes((e) => [
      ...e,
      {
        id: pendingId,
        channelId: p.channelId,
        userId: p.me,
        createAt,
        updateAt: createAt,
        deleteAt: 0,
        message,
        rootId: null,
        pendingId,
        state: PostState.Pending,
        type: '',
        props: null,
        isPinned: false,
        editAt: 0,
        embedJson: null,
        priority: extras.priority?.priority ?? '', // the Mac's pending row carries it too
      },
    ]);
    if (anchored) p.onLeaveJump();
    scroller.current?.scrollTo({ top: 0 });
    rememberEchoFiles(pendingId, p.channelId, extras.fileIds); // the echo draws its attachments too
    void data.call('send', p.channelId, message, null, pendingId, createAt, extras).then((r) => {
      if (r.refused) console.warn('[satori] refused:', r.refused);
    });
  };

  const actions: RowActions = useRowActions({
    onOpenThread: p.onOpenThread,
    onOpenUserId: p.onOpenUserId,
    onToast: p.onToast,
    teamName: p.teamName,
    quickReactions,
  });

  const { knownUsernames, fullNames: mentionFullNames } = useMentionDirectory(model?.knownUsernames, p.myUsername);
  // `onOpenUser` is read at click time: a new parent callback must not give every row a new ctx.
  // A mention opens the DIRECT conversation; the profile card is the fallback (features/threads).
  const openMention = useMentionOpener(p.onOpenUser);
  const openUser = useRef(openMention);
  openUser.current = openMention;
  const ctx: BodyContext = useMemo(
    () => ({
      knownUsernames,
      mentionFullNames,
      currentUsername: p.myUsername,
      customEmoji: current?.customEmoji ?? NO_CUSTOM_EMOJI,
      onMention: (username: string, anchor?: { x: number; y: number }) => openUser.current(username, anchor),
    }),
    [knownUsernames, mentionFullNames, p.myUsername, current?.customEmoji],
  );

  const savedSet = useMemo(() => new Set(current?.saved ?? []), [current?.saved]);
  const postPermissions = useConversationPermissions(p.channelId);
  const participants = useMemo(() => (model ? [...model.usersById.values()].filter((u) => u.id !== p.me) : []), [model, p.me]);
  const topState = ConversationLanding.topState({ isLoadingOlder: olderState.loading, olderLoadFailed: olderState.failed, hasMoreOlder: olderState.hasMore || (current ? current.posts.length >= limit : true) });
  const typingText = TypingIndicatorLabel.text(
    p.typists.map((id) => {
      const u = model?.usersById.get(id);
      return u ? userDisplayName(u) : t('mac.unknownAuthor');
    }),
    [],
  );

  const openIdentity = (e: ReactMouseEvent<HTMLElement>) => {
    if (!p.dmPartner) return p.onOpenInfo('info');
    const r = e.currentTarget.getBoundingClientRect();
    p.onOpenUserId(p.dmPartner.userId, { x: r.left, y: r.bottom + 6 });
  };

  return (
    <section className="conversation-pane" aria-label={p.title}>
      <div className="wallpaper" />
      <header className="conversation-header">
        {p.headerLeading}
        {/* Face and name are one target, as on the Mac: a DM opens the partner's profile (email, position,
            local time), any other conversation its info panel. The DM subtitle keeps its own device drill-in. */}
        <button type="button" className="header-identity" onClick={openIdentity} title={p.dmPartner ? t('web.channelPanel.openProfile') : t('web.nav.info')}>
          {p.headerFace ? (
            <span className="header-face">{p.headerFace}</span>
          ) : (
            <span style={{ color: 'var(--text-muted)', display: 'grid' }}>
              <Icon name={p.icon === 'lock' ? 'lock' : p.icon === 'hash' ? 'hash' : p.icon === 'area' ? 'people' : p.icon === 'group' ? 'threads' : 'at'} size={18} />
            </span>
          )}
        </button>
        <div className="title-block">
          <div className="title-row">
            <button type="button" className="header-identity header-title" onClick={openIdentity} title={p.title}>
              <h1>{p.title}</h1>
            </button>
            {p.dmPartner ? dmHeader.glyph : null}
          </div>
          {p.dmPartner ? dmHeader.subtitle : p.subtitle ? <div className="subtitle">{p.subtitle}</div> : null}
          {p.icon === 'dm' ? null : <ViewingStack channelId={p.channelId} rootId="" me={p.me} size={16} className="inline" />}
        </div>
        <div className="actions">
          {p.icon === 'dm' ? null : <ViewingStack channelId={p.channelId} rootId="" me={p.me} />}
          <button className="icon-button" onClick={p.onSearchHere} title={t('web.nav.searchHere')} aria-label={t('web.nav.searchHere')}>
            <Icon name="search" size={17} />
          </button>
          <button className="icon-button" onClick={() => p.onOpenInfo('pinned')} title={t('channel.pinned.title')} aria-label={t('channel.pinned.title')}>
            <Icon name="pin" size={17} />
          </button>
          <button className="icon-button" onClick={() => p.onOpenInfo('info')} title={t('web.nav.info')} aria-label={t('web.nav.info')}>
            <Icon name="info" size={17} />
          </button>
          {/* An archived channel takes no call and no Meet (it posts a message); «Registro» stays — it answers
              with an ephemeral post and is offered in every chat (owner ruling 2026-10-14, PLUGINS.md §9.5). */}
          {p.archived ? null : <CallHeaderButton channelId={p.channelId} channelName={p.title} />}
          <AppPluginActions channelId={p.channelId} channelName={p.title} onNotice={setPluginNotice} noMeet={!!p.archived} />
          <DiagnosticsButton channelId={p.channelId} />
        </div>
      </header>
      <BookmarksBar channelId={p.channelId} />
      {p.connectionStrip ? (
        <div className="connection-strip" role="status">
          <Icon name="retry" size={13} />
          {p.connectionStrip}
        </div>
      ) : null}
      <CallConversationStrips channelId={p.channelId} channelName={p.title}>
        {pluginNotice && pluginNotice.channelId === p.channelId ? <PluginNoticeStrip text={pluginNotice.text} onClose={() => setPluginNotice(null)} /> : null}
      </CallConversationStrips>
      <div className="timeline" ref={scroller} onScroll={onScroll} onWheel={onWheel} onKeyDown={onTimelineKey}>
        <div className="timeline-inner">
          <div ref={topSentinel} />
          {model ? (
            <>
              {topState === 'loading' || (model.rows.length === 0 && !anchored && !reachedStart && !olderState.failed && fillPages.current < FILL_MAX_PAGES) ? <div className="top-of-history">{t('channel.loading.accessibility')}…</div> : null}
              {topState === 'failed' ? (
                <div className="top-of-history">
                  <button onClick={() => askOlder(true, true)}>{t('channel.history.retry')}</button>
                </div>
              ) : null}
              {topState === 'beginning' && model.rows.length ? <div className="top-of-history">{t('channel.history.beginning')}</div> : null}
              {model.rows.length === 0 && HistoryFill.showsEmptyState(fillRoots, reachedStart) && !p.archived ? (
                <div className="empty-state">
                  <h2>{t('channel.empty.title')}</h2>
                  <p>{t('channel.empty.description')}</p>
                </div>
              ) : null}
              {model.rows.map((row) => {
                const author = model.usersById.get(row.post.userId);
                return (
                  <MessageRow
                    key={rowKeys.key(row.post)}
                    row={row}
                    authorName={model.authorName(row.post)}
                    authorVersion={author?.lastPictureUpdate ?? 0}
                    authorIsBot={author?.isBot === true}
                    presence={current?.statuses[row.post.userId] ?? null}
                    isOwn={row.post.userId === p.me}
                    editRights={editRights(row.post, p.me, postPermissions)}
                    files={current?.files[row.post.id]}
                    chips={model.chips.get(row.post.id)}
                    footer={model.footers.get(row.post.id)}
                    footerNames={footerNames}
                    seen={model.seen.get(row.post.id) ?? null}
                    saved={savedSet.has(row.post.id)}
                    marked={markedId === row.post.id}
                    ack={current?.acks?.[row.post.id]}
                    me={p.me}
                    ctx={ctx}
                    actions={actions}
                    now={now}
                  />
                );
              })}
            </>
          ) : null}
        </div>
      </div>
      {typingText ? (
        <div className="typing" role="status">
          <span className="typing-dots">
            <i />
            <i />
            <i />
          </span>
          {typingText}
        </div>
      ) : null}
      <DayPill scroller={scroller} />
      {anchored ? (
        <button
          className="jump-pill"
          onClick={() => {
            p.onLeaveJump();
            requestAnimationFrame(() => scroller.current?.scrollTo({ top: 0 }));
          }}
        >
          <Icon name="arrowDown" size={14} />
          {t('web.nav.backToRecents')}
        </button>
      ) : !atBottom ? (
        <button className="jump-pill" onClick={scrollToBottom}>
          <Icon name="arrowDown" size={14} />
          {t('channel.jump.toNewest')}
        </button>
      ) : null}
      {current ? (
      <Composer
        key={p.channelId}
        channelId={p.channelId}
        rootId={null}
        banner={model && model.failedCount > 0 ? <UndeliveredStrip channelId={p.channelId} failedCount={model.failedCount} onView={(id) => shell?.jumpToPost(id)} /> : null}
        channelKey={p.channelId}
        initialDraft={current?.draft ?? ''}
        placeholder={t('mac.composerPlaceholder')}
        participants={participants}
        customEmojiNames={Object.keys(current?.customEmoji ?? {})}
        onSend={send}
        onDraft={(m) => void data.call('saveDraft', p.channelId, '', m)}
        onEdit={actions.onEdit}
        onTyping={() => void data.call('userTyping', p.channelId, '')}
        disabledNotice={p.readOnlyNotice}
      />
      ) : null}
    </section>
  );
}
